import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createQuestion, getQuestion, updateQuestion } from '../services/api';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  PenLine,
  Sparkles,
  CheckCircle2,
  Eye,
  X,
  Plus,
  GripVertical,
  ArrowLeft,
  Save,
} from 'lucide-react';
import { toast } from 'react-toastify';

const typeDescriptions = {
  nps: 'Usada para medir satisfação geral (0–10).',
  escala: 'Usada para medir satisfação (1–5).',
  texto: 'Resposta aberta do cliente.',
  sim_nao: 'Resposta rápida e objetiva.',
  multipla_escolha: 'Seleciona uma única opção de uma lista.',
  multipla_escolha_multi: 'Permite selecionar uma ou mais opções.',
};

function OptionChip({ label, onRemove, dragHandleProps, draggableProps, innerRef }) {
  return (
    <span
      ref={innerRef}
      {...draggableProps}
      className="
        relative inline-flex items-center gap-1.5
        px-2.5 py-1 text-xs
        rounded-lg border border-slate-200
        bg-white text-slate-700 font-medium
        shadow-sm
        hover:border-[#2E8B57]/30 hover:bg-[#EAF7EE]/40
        transition
      "
    >
      <span
        {...dragHandleProps}
        className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-[#1F5C3A]"
        title="Arrastar para reordenar"
      >
        <GripVertical size={12} />
      </span>

      <span className="truncate max-w-[160px]">{label}</span>

      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remover ${label}`}
        className="
          -mr-1 inline-flex items-center justify-center
          w-4 h-4 rounded-full
          text-[#A5342B] hover:text-white hover:bg-[#A5342B]
          focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A5342B]
          transition
        "
      >
        <X size={11} strokeWidth={2.5} />
      </button>
    </span>
  );
}

const ToggleCard = ({ icon: Icon, title, description, checked, onChange }) => (
  <label
    className={`flex items-start gap-3 rounded-xl border p-3.5 cursor-pointer transition ${
      checked
        ? 'border-[#2E8B57]/40 bg-[#EAF7EE]/40 shadow-[0_2px_8px_rgba(46,139,87,0.08)]'
        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
    }`}
  >
    <div className="flex items-center gap-2 min-w-0 flex-1">
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition ${
          checked ? 'bg-[#2E8B57] text-white' : 'bg-slate-100 text-slate-500'
        }`}
      >
        <Icon size={16} />
      </div>
      <div className="min-w-0 flex-1">
        <div
          className={`text-sm font-semibold leading-tight ${
            checked ? 'text-[#1F5C3A]' : 'text-slate-700'
          }`}
        >
          {title}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 leading-snug">{description}</div>
      </div>
    </div>
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="sr-only"
    />
    {/* Custom toggle switch */}
    <div
      className={`relative w-9 h-5 rounded-full transition flex-shrink-0 mt-1 ${
        checked ? 'bg-[#2E8B57]' : 'bg-slate-300'
      }`}
    >
      <div
        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${
          checked ? 'left-[18px]' : 'left-0.5'
        }`}
      />
    </div>
  </label>
);

const QuestionEditor = () => {
  const { id } = useParams();
  const isNew = id === 'novo';
  const navigate = useNavigate();

  const [texto, setTexto] = useState('');
  const [tipo, setTipo] = useState('nps');
  const [obrigatoria, setObrigatoria] = useState(true);
  const [ativo, setAtivo] = useState(true);
  const [saving, setSaving] = useState(false);

  const [optionText, setOptionText] = useState('');
  const [options, setOptions] = useState([]);

  useEffect(() => {
    (async () => {
      if (!isNew && id) {
        try {
          const q = await getQuestion(Number(id));
          setTexto(q.texto || '');
          setTipo(q.tipo || 'nps');
          setObrigatoria(!!q.obrigatoria);
          setAtivo(!!q.ativo);
          setOptions(Array.isArray(q.opcoes) ? q.opcoes.map(String) : []);
        } catch (e) {
          console.error(e);
          toast.error('Não foi possível carregar a pergunta.');
        }
      }
    })();
  }, [id, isNew]);

  const addOption = () => {
    const v = optionText.trim();
    if (!v) return;
    if (options.includes(v)) {
      toast.info('Essa opção já foi adicionada.');
      return;
    }
    setOptions((prev) => [...prev, v]);
    setOptionText('');
  };

  const removeOption = (v) => setOptions((prev) => prev.filter((o) => o !== v));

  const typeNeedsOptions = ['multipla_escolha', 'multipla_escolha_multi'].includes(tipo);
  const typeHelp = typeDescriptions[tipo] || 'Define como a pergunta aparece no formulário.';

  const onSave = async () => {
    if (!texto.trim()) {
      toast.error('Digite o texto da pergunta antes de salvar.');
      return;
    }
    if (typeNeedsOptions && options.length === 0) {
      toast.error('Adicione pelo menos uma opção para este tipo de pergunta.');
      return;
    }
    setSaving(true);
    try {
      const payload = { texto, tipo, obrigatoria, ativo };
      if (typeNeedsOptions) payload.opcoes = options;
      if (isNew) await createQuestion(payload);
      else await updateQuestion(Number(id), payload);
      toast.success(isNew ? 'Pergunta criada!' : 'Pergunta atualizada!');
      navigate('/feedback-hero/form');
    } catch (err) {
      console.error(err);
      toast.error('Não foi possível salvar a pergunta.');
    } finally {
      setSaving(false);
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const next = Array.from(options);
    const [removed] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, removed);
    setOptions(next);
  };

  return (
    <div className="px-2 sm:px-6 pb-12 sm:pb-16">
      <div
        className="max-w-[1280px] mx-auto rounded-2xl sm:rounded-3xl border p-3 sm:p-5 md:p-6 overflow-hidden space-y-5"
        style={{
          borderColor: 'rgba(15, 23, 42, 0.06)',
          background:
            'radial-gradient(900px circle at 10% -20%, rgba(251, 236, 214, 0.55) 0%, transparent 55%), radial-gradient(1000px circle at 90% 0%, rgba(214, 234, 248, 0.55) 0%, transparent 60%), #F6F4F1',
        }}
      >
      {/* Hero header */}
      <div className="flex flex-col items-center text-center gap-1.5 px-2 pt-2">
        <div className="flex items-center gap-2">
          <PenLine size={22} className="text-[#1F5C3A]" />
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">
            {isNew ? 'Nova pergunta' : 'Editar pergunta'}
          </h2>
        </div>
        <p className="text-sm text-slate-500 max-w-xl leading-relaxed">
          Defina como esta pergunta aparece no formulário e qual o impacto para o cliente.
        </p>
      </div>

      {/* Form card */}
      <div className="rounded-2xl border border-slate-200/70 bg-white/95 backdrop-blur-sm shadow-[0_4px_16px_rgba(15,23,42,0.04)] p-5 sm:p-6 space-y-5">
        {/* Texto da pergunta */}
        <div className="space-y-1.5">
          <label
            htmlFor="texto-pergunta"
            className="block text-[11px] font-bold uppercase tracking-wider text-slate-500"
          >
            Texto da pergunta
          </label>
          <input
            id="texto-pergunta"
            className="w-full border border-slate-200 rounded-lg px-3.5 py-2.5 text-[15px] text-slate-800 bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2E8B57]/30 focus:border-[#2E8B57] transition"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Ex: Como você avalia o atendimento recebido hoje?"
          />
          <p className="text-[11px] text-slate-400">
            Esta é a frase que o cliente verá no formulário.
          </p>
        </div>

        {/* Tipo */}
        <div className="space-y-1.5">
          <label
            htmlFor="tipo-pergunta"
            className="block text-[11px] font-bold uppercase tracking-wider text-slate-500"
          >
            Tipo de resposta
          </label>
          <div className="relative">
            <select
              id="tipo-pergunta"
              className="w-full appearance-none border border-slate-200 rounded-lg pl-3.5 pr-9 py-2.5 text-[15px] text-slate-800 bg-white font-medium focus:outline-none focus:ring-2 focus:ring-[#2E8B57]/30 focus:border-[#2E8B57] transition cursor-pointer"
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
            >
              <option value="nps">Escala (0–10)</option>
              <option value="escala">Escala (1–5)</option>
              <option value="multipla_escolha">Múltipla escolha (uma opção)</option>
              <option value="multipla_escolha_multi">Múltipla seleção (checkbox)</option>
              <option value="sim_nao">Sim / Não</option>
              <option value="texto">Texto</option>
            </select>
            <svg
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          <p className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 mt-1">
            <Sparkles size={12} className="text-[#2E8B57]" />
            {typeHelp}
          </p>
        </div>

        {/* Opções (se aplicável) */}
        {typeNeedsOptions && (
          <div className="space-y-2 p-4 rounded-xl bg-[#EAF7EE]/30 border border-[#2E8B57]/15">
            <label
              htmlFor="opt-input"
              className="block text-[11px] font-bold uppercase tracking-wider text-[#1F5C3A]"
            >
              Opções de resposta
            </label>

            <div className="flex gap-2">
              <input
                id="opt-input"
                className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2E8B57]/30 focus:border-[#2E8B57] transition"
                value={optionText}
                onChange={(e) => setOptionText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addOption();
                  }
                }}
                placeholder="Digite uma opção e pressione Enter"
              />
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#2E8B57] text-white text-sm font-semibold hover:bg-[#247044] transition shadow-[0_4px_12px_rgba(46,139,87,0.18)]"
                onClick={addOption}
              >
                <Plus size={14} />
                Adicionar
              </button>
            </div>

            {options.length > 0 ? (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="opts-droppable" direction="horizontal">
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="flex flex-wrap gap-1.5 p-2.5 rounded-lg bg-white border border-slate-200 min-h-[3rem]"
                    >
                      {options.map((opt, index) => (
                        <Draggable
                          draggableId={`${opt}-${index}`}
                          index={index}
                          key={`${opt}-${index}`}
                        >
                          {(dragProvided) => (
                            <OptionChip
                              label={opt}
                              onRemove={() => removeOption(opt)}
                              innerRef={dragProvided.innerRef}
                              draggableProps={dragProvided.draggableProps}
                              dragHandleProps={dragProvided.dragHandleProps}
                            />
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              <p className="text-xs text-slate-500 italic">
                Nenhuma opção adicionada. Comece digitando acima.
              </p>
            )}
          </div>
        )}

        {/* Toggles */}
        <div className="grid gap-2.5 sm:grid-cols-2">
          <ToggleCard
            icon={CheckCircle2}
            title="Obrigatória"
            description="O cliente não pode pular esta pergunta."
            checked={obrigatoria}
            onChange={(e) => setObrigatoria(e.target.checked)}
          />
          <ToggleCard
            icon={Eye}
            title="Ativa no formulário"
            description="A pergunta aparece para o cliente."
            checked={ativo}
            onChange={(e) => setAtivo(e.target.checked)}
          />
        </div>
      </div>

      {/* Action bar */}
      <div className="flex items-center justify-between gap-2">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition"
        >
          <ArrowLeft size={14} />
          Cancelar
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-[#2E8B57] text-white text-sm font-semibold hover:bg-[#247044] transition shadow-[0_4px_12px_rgba(46,139,87,0.18)] hover:shadow-[0_6px_16px_rgba(46,139,87,0.28)] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Save size={14} />
          {saving ? 'Salvando…' : isNew ? 'Criar pergunta' : 'Salvar alterações'}
        </button>
      </div>
      </div>
    </div>
  );
};

export default QuestionEditor;
